import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { sanitizeHtml } from '../../assets/utils/comm/sanitize';

/**
 * CKEditorWidget - CKEditor 5를 위한 모듈형 React 래퍼.
 *
 * 자바 코드 수정 없이 이미지 업로드 용량 제한(/maxUploadSize) 500 에러를
 * 방지하기 위해 프론트엔드에서 기본값을 설정하는 로직이 포함되어 있습니다.
 *
 * @param {Object} props
 * @param {Object} props.data - 에디터 설정 데이터
 * @param {string} props.data.content - 초기 내용
 * @param {number} props.data.editorHeight - 에디터 높이 (px)
 * @param {string} props.data.id - 에디터 고유 ID
 * @param {Function} props.onChange - 내용 변경 시 콜백
 */
const CKEditorWidget = forwardRef(({ data, onChange }, ref) => {
  const editorContainerRef = useRef(null);
  const editorInstanceRef = useRef(null);

  const initialContent = sanitizeHtml(data?.content || '');
  const initialContentRef = useRef(initialContent);
  const editorHeight = data?.editorHeight || 450;
  const editorId = data?.id || 'ck-editor';
  const readOnly = data?.readOnly === true;
  const hideToolbar = data?.hideToolbar === true;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // 초기 로딩 여부 확인용
  const isLoadedRef = useRef(false);

  useEffect(() => {
    const initEditor = async () => {
      // 1. 필요한 전역 변수 및 설정 로딩 확인
      if (!window.ClassicEditor || !window.editorConfig) {
        try {
          // Webpack이 무시하고 브라우저가 직접 로드하도록 설정
          await import(/* webpackIgnore: true */ '/assets/ckeditor/ckeditor5-config.js');
        } catch (err) {
          console.error('[CKEditorWidget] Failed to load CKEditor 5 configuration module.', err);
          return;
        }
      }

      // 2. MyUploadAdapter의 maxUploadSize를 프론트엔드에서 강제 설정 (500 에러 방지)
      if (window.MyUploadAdapter && (window.MyUploadAdapter.maxUploadSize === null || window.MyUploadAdapter.maxUploadSize === undefined)) {
        window.MyUploadAdapter.maxUploadSize = 10 * 1024 * 1024; // 기본 10MB
      }

      const { ClassicEditor, editorConfig } = window;

      if (editorContainerRef.current && !editorInstanceRef.current) {
        try {
          const editor = await ClassicEditor.create(editorContainerRef.current, {
            ...editorConfig,
            initialData: initialContentRef.current,
          });

          // 에디터 높이 설정
          editor.editing.view.change((writer) => {
            writer.setStyle('min-height', `${editorHeight}px`, editor.editing.view.document.getRoot());
          });

          // 데이터 변경 시 onChange 호출
          editor.model.document.on('change:data', () => {
            const content = editor.getData();
            if (onChangeRef.current) {
              onChangeRef.current(content);
            }
          });

          if (readOnly) {
            editor.enableReadOnlyMode('viewer');
          }
          if (hideToolbar && editor.ui?.view?.toolbar?.element) {
            editor.ui.view.toolbar.element.style.display = 'none';
          }

          // 3. 업로드 어댑터 설정 (window.Editor는 editor-common.js에서 제공)
          const EditorUtil = window.Editor;
          if (EditorUtil && typeof EditorUtil.editorUploadAdapter === 'function') {
            EditorUtil.editorUploadAdapter(editor, editorId);
          }

          editorInstanceRef.current = editor;
          isLoadedRef.current = true;
        } catch (error) {
          console.error('[CKEditorWidget] CKEditor 5 initialization failed.', error);
        }
      }
    };

    initEditor();

    // 언마운트 시 정리
    return () => {
      if (editorInstanceRef.current) {
        editorInstanceRef.current
          .destroy()
          .then(() => {
            editorInstanceRef.current = null;
            isLoadedRef.current = false;
          })
          .catch((err) => {
            console.warn('[CKEditorWidget] Error during editor destruction.', err);
          });
      }
    };
  }, [editorHeight, editorId, readOnly, hideToolbar]); // initialContent는 initialContentRef로 고정하여 재생성 방지

  // initialContent가 비동기로 로드될 경우를 대비한 동기화 logic (최초 1회만)
  useEffect(() => {
    if (editorInstanceRef.current && isLoadedRef.current && initialContent && !editorInstanceRef.current.getData()) {
      editorInstanceRef.current.setData(sanitizeHtml(initialContent));
    }
  }, [initialContent]);

  useImperativeHandle(ref, () => ({
    focusEditor: () => {
      const editor = editorInstanceRef.current;
      if (!editor) return false;
      try {
        if (editor.editing?.view?.focus) {
          editor.editing.view.focus();
          return true;
        }
        if (editor.focus) {
          editor.focus();
          return true;
        }
      } catch (error) {
        console.warn('[CKEditorWidget] focusEditor failed.', error);
      }
      return false;
    },
    getEditor: () => editorInstanceRef.current,
  }), []);

  return (
    <div
      id={`${editorId}-wrapper`}
      className={`ck-editor-widget-container${readOnly ? ' ck-editor-widget--readonly' : ''}`}
    >
      <div ref={editorContainerRef} id={editorId}></div>
      {!readOnly ? (
        <p className="ck-editor-license-note">
          Editor powered by{' '}
          <a href="https://ckeditor.com/ckeditor-5" target="_blank" rel="noopener noreferrer">
            CKEditor 5
          </a>{' '}
          (GPL-2.0).
        </p>
      ) : null}
      <style>{`
        .ck-editor-widget-container {
          margin-bottom: 0.1rem;
          background: #fff;
          border-radius: 4px;
        }
        .ck-editor__editable {
          resize: vertical;
          overflow: auto;
        }
        .ck-editor__editable_inline {
          min-height: ${editorHeight}px;
        }
        .ck.ck-editor__main > .ck-editor__editable {
          background: #fff;
          border-color: #e2e8f0;
        }
        .ck.ck-toolbar {
          background: #fff;
          border-color: #e2e8f0;
        }
        /*
         * 전역 style.css의 ul { list-style:none; padding:0 } 보정.
         * .ck-content는 에디터 외 영역에도 붙을 수 있어, 본문 영역만 지정해 툴바/드롭다운 li 레이아웃이 깨지지 않게 함.
         */
        .ck-editor-widget-container .ck.ck-editor__main > .ck-editor__editable ul {
          list-style-type: disc;
          list-style-position: outside;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ck-editor-widget-container .ck.ck-editor__main > .ck-editor__editable ol {
          list-style-type: decimal;
          list-style-position: outside;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ck-editor-widget-container .ck.ck-editor__main > .ck-editor__editable ul ul {
          list-style-type: circle;
        }
        .ck-editor-widget-container .ck.ck-editor__main > .ck-editor__editable ul ul ul {
          list-style-type: square;
        }
        .ck-editor-license-note {
          margin: 8px 2px 0;
          font-size: 12px;
          line-height: 1.4;
          color: #64748b;
          text-align: right;
        }
        .ck-editor-license-note a {
          color: inherit;
          text-decoration: underline;
        }
        .ck-editor-widget--readonly .ck.ck-editor__main > .ck-editor__editable {
          resize: none !important;
        }
        .ck-editor-widget--readonly .ck.ck-editor__main > .ck-editor__editable::-webkit-resizer {
          display: none;
        }
      `}</style>
    </div>
  );
});

export default CKEditorWidget;
